import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { ShieldCheck, Loader2, Phone } from "lucide-react";
import { api } from "../services/api";

interface OtpVerificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerified: (otpToken: string) => void;
  defaultPhone?: string;
}

const OTP_LENGTH = 6;
const COUNTDOWN_SECONDS = 60;

const OtpVerificationModal: React.FC<OtpVerificationModalProps> = ({
  open,
  onOpenChange,
  onVerified,
  defaultPhone = "",
}) => {
  const { t } = useTranslation(["documents", "common"]);
  const [phone, setPhone] = useState(defaultPhone);
  const [otpCode, setOtpCode] = useState("");
  const [otpToken, setOtpToken] = useState<string | null>(null);
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setPhone(defaultPhone);
      setOtpCode("");
      setOtpToken(null);
      setStep("phone");
      setError(null);
      setCountdown(0);
      setIsSending(false);
      setIsVerifying(false);
    }
  }, [open, defaultPhone]);

  // Auto-focus code input when step changes to "code"
  useEffect(() => {
    if (step === "code" && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleSendOtp = async () => {
    if (!phone.trim()) return;
    setIsSending(true);
    setError(null);

    try {
      const response = await api.post<{ token: string; expires_at: string }>(
        "/otp/send",
        { phone: phone.trim() }
      );
      const data = (response as any).token
        ? (response as any)
        : response.data;
      setOtpToken(data.token);
      setStep("code");
      setCountdown(COUNTDOWN_SECONDS);
    } catch (err: any) {
      setError(err.message || t("documents:otp_send_failed"));
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpToken || otpCode.length !== OTP_LENGTH) return;
    setIsVerifying(true);
    setError(null);

    try {
      const response = await api.post<{ verified: boolean }>(
        "/otp/verify",
        { token: otpToken, code: otpCode }
      );
      const data = (response as any).verified !== undefined
        ? (response as any)
        : response.data;

      if (data.verified) {
        onVerified(otpToken);
        onOpenChange(false);
      } else {
        setError(t("documents:otp_invalid_code"));
      }
    } catch (err: any) {
      setError(err.message || t("documents:otp_verify_failed"));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = () => {
    setOtpCode("");
    setError(null);
    handleSendOtp();
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH);
    setOtpCode(value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle>{t("documents:otp_title")}</DialogTitle>
              <DialogDescription>
                {step === "phone"
                  ? t("documents:otp_phone_description")
                  : t("documents:otp_code_description")}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {step === "phone" ? (
            <div className="space-y-2">
              <Label htmlFor="otp-phone">{t("documents:otp_phone_label")}</Label>
              <div className="flex items-center border rounded-md focus-within:ring-1 focus-within:ring-primary">
                <div className="px-3 py-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                </div>
                <Input
                  id="otp-phone"
                  type="tel"
                  placeholder={t("documents:otp_phone_placeholder")}
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="otp-code">{t("documents:otp_code_label")}</Label>
                <Input
                  id="otp-code"
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={OTP_LENGTH}
                  value={otpCode}
                  onChange={handleCodeChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && otpCode.length === OTP_LENGTH) {
                      handleVerifyOtp();
                    }
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {t("documents:otp_sent_to", { phone })}
              </p>
            </div>
          )}

          {error && (
            <p className="text-sm text-destructive text-center">{error}</p>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          {step === "phone" ? (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button
                onClick={handleSendOtp}
                disabled={!phone.trim() || isSending}
              >
                {isSending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t("documents:otp_send_code")}
              </Button>
            </>
          ) : (
            <>
              <div className="flex-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResend}
                  disabled={countdown > 0 || isSending}
                  className="text-xs"
                >
                  {countdown > 0
                    ? t("documents:otp_resend_countdown", { seconds: countdown })
                    : t("documents:otp_resend")}
                </Button>
              </div>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {t("common:actions.cancel")}
              </Button>
              <Button
                onClick={handleVerifyOtp}
                disabled={otpCode.length !== OTP_LENGTH || isVerifying}
              >
                {isVerifying && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {t("documents:otp_verify")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OtpVerificationModal;
